// src/api/axiosInstance.ts
import axios from "axios";


export const BASE_URL = import.meta.env.VITE_API_BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // Include cookies in cross-origin requests
});

// Flag to prevent infinite retry loops
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // localStorage unavailable (e.g. Safari private mode)
  }

  // Dev guardrail: the Django backend has APPEND_SLASH enabled, so a request to a
  // slash-less path gets 301-redirected to the slashed route — and the browser
  // downgrades the redirected POST/PUT/PATCH to a GET (dropping the body), which
  // surfaces as a confusing 405. Warn early so it's caught before it ships.
  if (import.meta.env.DEV && config.url) {
    const path = config.url.split("?")[0].split("#")[0];
    if (path.startsWith("/") && !path.endsWith("/")) {
      console.warn(
        `[api] Request path "${path}" is missing a trailing slash. ` +
          `Django's APPEND_SLASH will 301-redirect it and downgrade ` +
          `${(config.method ?? "get").toUpperCase()} → GET (405). Add the trailing slash.`
      );
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 Unauthorized and not already retrying.
    // Skip the refresh cycle for auth endpoints — they intentionally return 401
    // and the caller (LoginPage, etc.) handles the error message itself.
    const isAuthEndpoint = originalRequest.url?.includes("/api/users/login/") ||
      originalRequest.url?.includes("/api/users/token/");
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Wait until refresh finishes
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // _retry: true prevents the interceptor from recursively handling this call
        const res = await api.post("/api/users/token/refresh/", {}, { _retry: true } as any);

        const newAccessToken = res.data.access;
        localStorage.setItem("access_token", newAccessToken);
        api.defaults.headers.common["Authorization"] = "Bearer " + newAccessToken;

        processQueue(null, newAccessToken);
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token"); // clean up any legacy data
        localStorage.removeItem("user");
        delete api.defaults.headers.common["Authorization"];
        window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
